import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {KennethMustDie} from "../KennethMustDie";
import {gsap} from "gsap";
import {
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData
} from "../../core/reel/simplereel/SimpleReelModel";
import {ArrayUtils} from "../utils/ArrayUtils";
import {KMDSymbol} from "../reels/KMDSymbol";
import {SPECIAL_SYMBOL_NAMES} from "../server/IKMDData";
import {KMDSound} from "../sound/KMDSound";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export class KMDReelStopPresentation implements FlowStatePresentation {
    private _tl: gsap.core.Timeline;
    private _stoppedReels: boolean[];

    public start(flowStateController: FlowStateController): void {
        flowStateController.setSkipCallback(() => this.skip());
        this._stoppedReels = [false, false, false, false, false, false];

        KennethMustDie.s1CountReelStop = 0;

        this._tl = gsap.timeline();

        KennethMustDie.specialReelView.reel.createStopSymbolsForSpecialReel();

        this._tl.add(KennethMustDie.multipliersChain.onResult(true));

        let stopTime: number = this._tl.totalDuration();
        for (let i: number = 0; i < KennethMustDie.model.gameData.reels.length; i++) {
            const reelData: string[] = KennethMustDie.model.gameData.reels[i],
                reelStopAnimation: gsap.core.Timeline = gsap.timeline();

            reelStopAnimation.add(this.preventSkipOnLastReel(i, flowStateController));
            reelStopAnimation.add(KennethMustDie.reelView.reelModels[i].stop(reelData));

            KennethMustDie.reelView.reelModels[i].events?.once(SimpleReelModelState.STOPPED, (data: SimpleReelModelEventData) => this.onReelStop(data, flowStateController));
            KennethMustDie.reelView.reelModels[i].events?.once(SimpleReelModelState.BOUNCE, (data: SimpleReelModelEventData) => this.onReelBounce(data));

            this._tl.add(reelStopAnimation, stopTime);

            if (i === 3 && KennethMustDie.model.nearWinReels.includes(true) && !KennethMustDie.model.nearWinReels[4]) {
                this._tl.add(() => {
                    KennethMustDie.stopScattersAnticipation();
                });
            }

            stopTime += (reelStopAnimation.totalDuration());

            if (i > 0 && i < (KennethMustDie.REELS_NUM - 1)) {
                this._tl.add(KennethMustDie.specialReelView.reel.stopIdxWise(i - 1), stopTime);
            }

            // #233
            if (KennethMustDie.model.nearWinReels[i + 1]) {
                stopTime += KennethMustDie.SCATTER_NEAR_WIN_DELAY;

                if (i + 1 === 4) {
                    stopTime += 2;
                }

                if (KennethMustDie.model.xHPos === 1) {
                    stopTime += 2;
                }
            }
        }

        this._tl.add(() => {
            // #326
            KennethMustDie.updateWaysOnReelStopActions(KennethMustDie.model.multipliersForReelStop);
        });

        this._tl.to(this, {
            duration: 0.1
        });

        KennethMustDie.specialReelView.reel.events?.once(SimpleReelModelState.STOPPED, (data: SimpleReelModelEventData) => this.onReelStop(data, flowStateController));

        const padding: number = KennethMustDie.gameFlowController.calculatePaddingDuration(this._tl.totalDuration());
        if (padding > 0) {
            this._tl.shiftChildren((KennethMustDie.apiPlugin.gameClientConfiguration.minimumSpinTime / 1000) - this._tl.totalDuration()); // #458, calculatePaddingDuration doesn't give correct padding offset, I believe, could be because of decimals involved
        }

        if (KennethMustDie.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            this._tl.timeScale(2);
        }
    }

    private preventSkipOnLastReel(reelId: number, flowStateController: FlowStateController): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (reelId === 4) {
            tl.add(() => {
                flowStateController.setSkipCallback();
            }); //prevents useless skips when last reel has started its stop anyway.
        }
        return tl;
    }

    private skip(): void {
        this._tl.totalProgress(1);
    }

    private onReelBounce(data: SimpleReelModelEventData): void {
        // #226, #240
        const stopSymbols: SimpleReelModelSymbolData[] = KennethMustDie.reelView.reelModels[data.reel].stopSymbols;
        let sym: KMDSymbol;

        // traversing the visible symbols only
        for (let i: number = 1; i < stopSymbols.length - 1; i++) {
            sym = (stopSymbols[i].positionable as KMDSymbol);

            if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                KennethMustDie.soundPlayer.playSound(KMDSound.X_NUDGE_LAND);

                // #273
                sym.showMultiplierOnXNudge(1, -1);
            } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_WAYS) {
                KennethMustDie.soundPlayer.playSound(KMDSound.X_WAYS_LAND);
            } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.S) {
                // #508
                if (!(data.reel === 1 && KennethMustDie.buyFeatureName === "BOOSTED_BET")) {
                    KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_LAND[KennethMustDie.s1CountReelStop]);
                }

                KennethMustDie.s1CountReelStop++;
            } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.XH) {
                KennethMustDie.soundPlayer.playSound(KennethMustDie.model.xHPos === 1 ? KMDSound.X_HOLE_LAND : KMDSound.X_HOLE_LAND_MISS);
            } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.SM1 || sym.originalName === SPECIAL_SYMBOL_NAMES.SM2) { // #517
                sym.parent.addChild(sym);
            } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.S2 && KennethMustDie.s1CountReelStop === 3) {
                KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_LAND[3]);

                // #485
                if (!KennethMustDie.model.gameData.wasFeatureBuy) {
                    // #529
                    KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_FIRE);

                    KennethMustDie.nearWinAnticipation.playAnticipateFireAnimation(4, KennethMustDie.model.s2Feature.originSymId + 1, 0);
                }
            }
        }

        // #485
        if (KennethMustDie.s1CountReelStop > 1 && KennethMustDie.model.nearWinReels[data.reel + 1]) {
            // #529
            KennethMustDie.soundPlayer.playSound(KMDSound.SCATTER_FIRE);

            for (let reelId: number = 1; reelId <= data.reel; reelId++) {
                if (KennethMustDie.model.s1Positions[reelId] > -1) {
                    KennethMustDie.nearWinAnticipation.playAnticipateFireAnimation(reelId, KennethMustDie.model.s1Positions[reelId] + 1, 0);
                }
            }
        }

        // #207
        KennethMustDie.soundPlayer.playSound(KMDSound.REELS_LANDING[data.reel]);

        KennethMustDie.tryAnticipation(data.reel);

        if (data.reel === (KennethMustDie.REELS_NUM - 1)) {
            KennethMustDie.stopScattersAnticipation();

            // #235, #502
            KennethMustDie.soundPlayer.stopSound(KMDSound.REELS_SPINNING);
        }
    }

    private onReelStop(data: SimpleReelModelEventData, flowStateController: FlowStateController): void {
        this._stoppedReels[data.reel] = true;
        if (ArrayUtils.isAllTrue(this._stoppedReels)) {
            this.completePresentation(flowStateController);

            KennethMustDie.stopScattersAnticipation();

            // #235
            KennethMustDie.soundPlayer.stopSound(KMDSound.REELS_SPINNING);
        }
    }

    private completePresentation(flowStateController: FlowStateController): void {
        flowStateController.finish();
        this._tl.kill();
    }
}